import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import { Dayjs } from "dayjs";
import type { CSSProperties, FC, ReactElement, ReactNode } from "react";
import type { ServerStream } from "src/types/work-packets";
import type { ActivationState } from "./activation-status-types";
import { SunsetBannerBody, SunsetBannerTitle } from "./SunsetBannerText";
import { DeactivationBannerBody, DeactivationBannerTitle } from "./DeactivationBannerText";
import { ExpiredBannerBody, ExpiredBannerTitle } from "./ExpiredBannerText";

export interface ActivationStatusTooltipProps {
  activationState: ActivationState;
  deactivationDate?: Date;
  recoveryStream: ServerStream;
  expiredAt: Dayjs | undefined;
  children: ReactElement;
}

const tooltipSlotProps: TooltipProps["slotProps"] = { tooltip: { sx: { scale: 1.25 } } };
const hrStyle: CSSProperties = { marginBlock: "2px" };

export const ActivationStatusTooltip: FC<ActivationStatusTooltipProps> = ({
  activationState,
  deactivationDate,
  recoveryStream,
  expiredAt,
  children,
}) => {
  const tooltipContent = getTooltipContent(activationState, deactivationDate, recoveryStream, expiredAt);
  return (
    <Tooltip title={tooltipContent} followCursor slotProps={tooltipSlotProps}>
      {children}
    </Tooltip>
  );
};

interface ActivationStatusTooltipContentProps {
  title: ReactNode;
  subtitle?: ReactNode;
  body: ReactNode;
}

const ActivationStatusTooltipContent: FC<ActivationStatusTooltipContentProps> = ({ title, subtitle, body }) => (
  <div>
    <p>
      <b>{title}</b>
    </p>
    {subtitle && (
      <p>
        <i>{subtitle}</i>
      </p>
    )}
    <hr style={hrStyle} />
    <p>{body}</p>
  </div>
);

function getTooltipContent(
  state: ActivationState,
  deactivationDate: Date | undefined,
  stream: ServerStream,
  expiredAt: Dayjs | undefined,
) {
  if (state === "inactive") {
    return <ActivationStatusTooltipContent title={<DeactivationBannerTitle />} body={<DeactivationBannerBody />} />;
  }

  if (state === "sunset") {
    return (
      <ActivationStatusTooltipContent
        title={<SunsetBannerTitle scope={stream} />}
        subtitle={<>Since {deactivationDate?.toLocaleDateString("en-us", { dateStyle: "medium" })}</>}
        body={<SunsetBannerBody />}
      />
    );
  }

  if (expiredAt) {
    return (
      <ActivationStatusTooltipContent
        title={<ExpiredBannerTitle />}
        subtitle={<>Since {expiredAt?.toDate()?.toLocaleDateString("en-us", { dateStyle: "medium" })}</>}
        body={<ExpiredBannerBody />}
      />
    );
  }
}
